import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

export const fetchAppointments = async (token, veterinaryId) => {
  if (!token || !veterinaryId) {
    throw new Error("Token and veterinary ID are required");
  }

  const response = await axios.get(
    `${apiUrl}/api/v1/veterinaries/${veterinaryId}/appointments`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export const updateAppointmentStatus = async (
  token,
  appointmentId,
  serviceId,
  newStatus,
) => {
  if (!token || !appointmentId || !serviceId) {
    throw new Error("Token, appointment ID, and service ID are required");
  }

  const response = await axios.put(
    `${apiUrl}/api/v1/services/${serviceId}/appointments/${appointmentId}`,
    { appointment: { status: newStatus } },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export const updateAppointment = async (
  token,
  appointmentId,
  serviceId,
  updatedData,
) => {
  if (!token || !appointmentId) {
    throw new Error("Token and appointment ID are required");
  }

  const response = await axios.put(
    `${apiUrl}/api/v1/services/${serviceId}/appointments/${appointmentId}`,
    { appointment: updatedData },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export const deleteAppointment = async (token, appointmentId, serviceId) => {
  if (!token || !appointmentId || !serviceId) {
    throw new Error("Token, appointment ID, and service ID are required");
  }

  await axios.delete(
    `${apiUrl}/api/v1/services/${serviceId}/appointments/${appointmentId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const calculateAppointmentStats = (appointments) => {
  if (!appointments || appointments.length === 0) {
    return {
      todayCount: 0,
      upcomingNext: "--:--",
      confirmed: 0,
      confirmedTrend: "0%",
      confirmedTrendDirection: "neutral",
      pending: 0,
      pendingTrend: "0%",
      pendingTrendDirection: "neutral",
      cancelled: 0,
      cancelledTrend: "0%",
      cancelledTrendDirection: "neutral",
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const lastWeekStart = new Date(today);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(today);
  lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

  const validAppointments = appointments.filter((a) => a.datetime !== null);

  const todayAppointments = validAppointments
    .filter((a) => {
      const aptDate = new Date(a.datetime);
      aptDate.setHours(0, 0, 0, 0);
      return aptDate.getTime() === today.getTime();
    })
    .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

  const confirmedCount = appointments.filter(
    (a) => a.status === "confirmed",
  ).length;
  const pendingCount = appointments.filter(
    (a) => a.status === "pending",
  ).length;
  const cancelledCount = appointments.filter(
    (a) => a.status === "cancelled",
  ).length;

  const lastWeekConfirmed = validAppointments.filter((a) => {
    const aptDate = new Date(a.datetime);
    return (
      a.status === "confirmed" && aptDate >= lastWeekStart && aptDate < today
    );
  }).length;

  const lastWeekPending = validAppointments.filter((a) => {
    const aptDate = new Date(a.datetime);
    return (
      a.status === "pending" && aptDate >= lastWeekStart && aptDate < today
    );
  }).length;

  const lastWeekCancelled = validAppointments.filter((a) => {
    const aptDate = new Date(a.datetime);
    return (
      a.status === "cancelled" && aptDate >= lastWeekStart && aptDate < today
    );
  }).length;

  const calculateTrend = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const confirmedTrend = calculateTrend(confirmedCount, lastWeekConfirmed);
  const pendingTrend = calculateTrend(pendingCount, lastWeekPending);
  const cancelledTrend = calculateTrend(cancelledCount, lastWeekCancelled);

  return {
    todayCount: todayAppointments.length,
    upcomingNext:
      todayAppointments.length > 0
        ? new Date(todayAppointments[0].datetime).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "--:--",
    confirmed: confirmedCount,
    confirmedTrend:
      confirmedTrend !== 0 ? `${Math.abs(confirmedTrend)}%` : "0%",
    confirmedTrendDirection:
      confirmedTrend > 0 ? "up" : confirmedTrend < 0 ? "down" : "neutral",
    pending: pendingCount,
    pendingTrend: pendingTrend !== 0 ? `${Math.abs(pendingTrend)}%` : "0%",
    pendingTrendDirection:
      pendingTrend > 0 ? "up" : pendingTrend < 0 ? "down" : "neutral",
    cancelled: cancelledCount,
    cancelledTrend:
      cancelledTrend !== 0 ? `${Math.abs(cancelledTrend)}%` : "0%",
    cancelledTrendDirection:
      cancelledTrend > 0 ? "up" : cancelledTrend < 0 ? "down" : "neutral",
  };
};

export const filterAppointments = (
  appointments,
  searchQuery,
  activeFilter,
  dateRange,
) => {
  if (!appointments) return [];

  return appointments.filter((appointment) => {
    if (!appointment.datetime) return false;

    const matchesSearch =
      searchQuery === "" ||
      appointment.pet?.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      appointment.pet?.user?.first_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      appointment.pet?.user?.last_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesFilter =
      activeFilter === "All" ||
      (activeFilter === "Upcoming today" &&
        new Date(appointment.datetime).toDateString() ===
          new Date().toDateString()) ||
      activeFilter.toLowerCase() === appointment.status;

    const appointmentDate = new Date(appointment.datetime);
    appointmentDate.setHours(0, 0, 0, 0);
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to) : null;

    const matchesDateRange =
      !fromDate ||
      !toDate ||
      (appointmentDate >= new Date(fromDate.setHours(0, 0, 0, 0)) &&
        appointmentDate <= new Date(toDate.setHours(23, 59, 59, 999)));

    return matchesSearch && matchesFilter && matchesDateRange;
  });
};

export const getPetTypeIcon = (petType) => {
  const PET_TYPE_ICONS = {
    dog: "Dog",
    cat: "Cat",
    fish: "Fish",
    bird: "Bird",
    rabbit: "Rabbit",
    turtle: "Turtle",
    rodent: "Star",
  };

  return PET_TYPE_ICONS[petType?.toLowerCase()] || "Star";
};

export const getPetTypeLabel = (petType) => {
  const labels = {
    dog: "Dog",
    cat: "Cat",
    fish: "Fish",
    bird: "Bird",
    rabbit: "Rabbit",
    turtle: "Turtle",
    rodent: "Rodent",
  };
  return labels[petType?.toLowerCase()] || "Pet";
};

export const getPaginatedAppointments = (
  appointments,
  currentPage,
  itemsPerPage,
) => {
  if (!appointments || appointments.length === 0) return [];

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  return appointments.slice(startIndex, endIndex);
};
